import DOMPurify from "dompurify";
import { Button } from "primereact/button";
import React, { useEffect, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import { Link } from "react-router-dom";
import FavoriteButton from "../../../../components/FavoriteButton/FavoriteButton";
import Loading from "../../../../components/Loading/Loading";
import ProductRating from "../../../../components/ProductRating/ProductRating";
import ReviewsModal from "../../../../components/ReviewsModal/ReviewsModal";
import ShareButton from "../../../../components/ShareButton/ShareButton";
import PromoterShareBox from "../../../../components/PromoterShare/PromoterShareBox";
import useCategories from "../../../../hooks/useCategories";
import useGetCommentsOfStore from "../../../../hooks/useGetCommentsOfStore";
import useRatingStore from "../../../../hooks/useRatingStore";
import useStoreName from "../../../../hooks/useStoreName";
import { useToggle } from "../../../../hooks/useToggle";
import localeText from "../../../../locale";
import showErrorToast from "../../../../utilities/Toast/showErrorToast";
import { locale } from "../../../../utilities/locale/locale";
import HeaderSection from "../../../Shop/components/HeaderSection/HeaderSection";
import BreadCrumbProduct from "../BreadCrumbProduct/BreadCrumbProduct";
import ButtonAddToCart from "../ButtonAddToCart/ButtonAddToCart";
import ComparePriceButton from "../ComparePriceButton/ComparePriceButton";
import GalleryImages from "../GalleryImages/GalleryImages";
import InputNumberStyled from "../InputNumberStyled/InputNumberStyled";
import MinimalPrice from "../MinimalPrice/MinimalPrice";
import ProductDescription from "../ProductDescription/ProductDescription";
import ProductPrice from "../ProductPrice/ProductPrice";
import ProductPromotionalPrice from "../ProductPromotionalPrice/ProductPromotionalPrice";
import ProductQuantity from "../ProductQuantity/ProductQuantity";
import ProductRatingComments from "../ProductRatingComments/ProductRatingComments";
import ProductStateTag from "../ProductStateTag/ProductStateTag";
import ProductVariations from "../ProductVariations/ProductVariations";
import RelationedProducts from "../RelationedProducts/RelationedProducts";

import { useHistory } from "react-router-dom";
import ProductQuestionsArea from "../ProductQuestionsArea/ProductQuestionsArea";
import "./ProductSection.css";
import MapLocationModal from "../MapLocationModal/MapLocationModal";
import ProductInfoFooter from "../ProductInfoFooter/ProductInfoFooter";
import { MiniAddToCart } from "../ProductCard/MiniAddToCart/MiniAddToCart";
import { useLocation } from "react-router-dom";

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.substring(1);
};

const ProductHeader = ({ selectedSubcategory, product }) => {
  const { categories } = useCategories();

  const currentCategory = useMemo(() => {
    return categories.find((category) => {
      const subCategoryOfCurrentCategory = category.subCategories.find(
        (subCategory) => subCategory.id === selectedSubcategory.id
      );

      if (subCategoryOfCurrentCategory) return category;
      return null;
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const labelCategory = {
    pt: currentCategory?.descriptionPortuguese,
    en: currentCategory?.descriptionEnglish,
    it: currentCategory?.descriptionItalian,
    es: currentCategory?.descriptionSpanish,
  };

  const labelSubcategory = {
    pt: selectedSubcategory?.descriptionPortuguese,
    en: selectedSubcategory?.descriptionEnglish,
    it: selectedSubcategory?.descriptionItalian,
    es: selectedSubcategory?.descriptionSpanish,
  };

  return (
    <>
      <BreadCrumbProduct
        selectedCategory={currentCategory}
        selectedSubcategory={selectedSubcategory}
        product={product}
      />
      {/*  <HeaderSection classNameTitle="pb-3" classNameContainer="xl:px-0" disableBoxModel={true}>
        {labelCategory[locale]}
      </HeaderSection> */}
    </>
  );
};

const ProductSection = ({
  productInfo,
  toast,
  currency,
  currentAttribute1Value,
  currentAttribute2Value,
  currentVariation,
  attribute1Values,
  attribute2Values,
  checkAndSetCurrentAttribute1Value,
  checkAndSetCurrentAttribute2Value,
  productQty,
  setProductQty,
  isFreeExtracoin,
}) => {
  const { product } = productInfo.data;

  const locationUrl = useLocation();

  const isFringeBenefitsPage = locationUrl?.pathname?.toLowerCase()?.includes("fringe");

  const { loadingStoreName, storeName } = useStoreName({
    username: product.user.username,
    onError: showErrorToast(toast.current),
  });

  const {
    value: isOpenStoreLocationModal,
    setAsFalse: closeStoreLocationModal,
    setAsTrue: openStoreLocationModal,
  } = useToggle();

  const { isLoading, commentsOfStore } = useGetCommentsOfStore({
    username: product?.user?.username,
    onError: showErrorToast(toast.current),
  });
  const { isLoading: isLoadingRatingOfStore, ratingOfStore } = useRatingStore({
    username: product?.user?.username,
    onError: showErrorToast(toast.current),
  });
  const history = useHistory();
  const location = history?.location;
  const loginPathName = location?.state?.from;
  const scrollRef = useRef();
  const productQuestionsRef = useRef();
  const { value, setAsFalse, setAsTrue } = useToggle();

  const currencySymbols = {
    USD: "$",
    EUR: "€",
    BRL: "R$",
    GBP: "£",
    JPY: "¥",
    CNY: "¥",
    AUD: "AU$",
    CAD: "CA$",
    CHF: "CHF",
  };

  const currencySymbol = currencySymbols[currency];

  useEffect(() => {
    if (
      loginPathName &&
      productQuestionsRef?.current &&
      localStorage.getItem("__extraconomy_username")
    )
      productQuestionsRef.current.scrollIntoView();
    else if (scrollRef.current) scrollRef.current.scrollIntoView();
  }, [loginPathName]);

  useEffect(() => {
    if (product?.downloadable) setProductQty(1); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (product.status !== "PUBLISHED") {
    return (
      <div
        className="text-center my-auto flex align-items-center justify-content-center flex-column"
        style={{ minHeight: "50vh" }}
      >
        <i className="pi pi-search text-8xl mb-1 text-400"></i>
        <h3 className="text-gray-600">{localeText("productNotFound")}</h3>
        <Link to="/shop">
          <Button label={localeText("shop")} className="font-bold text-base px-5 py-2" />
        </Link>
      </div>
    );
  }

  const freightType = productInfo?.data?.product?.shippingOption;
  const freightText = capitalizeFirstLetter(
    freightType === "PICKUP"
      ? localeText("paidOrWithdrawn")
      : freightType === "FREESENDCLOUD"
      ? localeText("freeToItaly")
      : freightType === "FREE"
      ? localeText("free")
      : localeText("paid")
  );

  const selectedSubcategory = product.subCategory;
  const imagens = [
    productInfo?.data?.product?.imageid1,
    productInfo?.data?.product?.imageid2,
    productInfo?.data?.product?.imageid3,
    productInfo?.data?.product?.imageid4,
    productInfo?.data?.product?.imageid5,
    productInfo?.data?.product?.imageid6,
    productInfo?.data?.product?.imageid7,
    productInfo?.data?.product?.imageid8,
    productInfo?.data?.product?.imageid9,
    productInfo?.data?.product?.imageid10,
  ];

  const imagesFiltered = imagens?.filter((image) => image !== null && image !== undefined);

  return (
    <div className="mt-4 mb-8" ref={scrollRef}>
      <MapLocationModal
        isOpen={isOpenStoreLocationModal}
        onClose={closeStoreLocationModal}
        store={productInfo?.data?.shopConfiguration}
      />

      <ProductHeader selectedSubcategory={selectedSubcategory} product={product?.title} />
      <div className="grid grid-nogutter">
        <div className="col-12 md:col-6 lg:col-6 img-gallery-padding hidden sm:block">
          <GalleryImages
            images={imagesFiltered}
            imagesVariations={productInfo?.imagesVariations}
            currentImageVariation={currentVariation?.imageid}
            views={productInfo?.data?.product?.views}
            productId={product?.id}
          />
        </div>

        <div className="bg-white w-full col flex justify-content-end">
          <div className="" style={{ width: "100%", maxWidth: "485px" }}>
            {!isFringeBenefitsPage && <ProductRating productId={product.id} product={product} />}

            <div className="">
              <div className="flex gap-0">
                <div className="flex align-items-center">
                  <span
                    className="sm:font-bold sm:text-2xl"
                    style={{
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                      color: "#091E42",
                    }}
                  >
                    {product.title}
                  </span>{" "}
                  {/* <ProductStateTag productInfo={productInfo} /> */}
                </div>
              </div>
              <div className="col-12 sm:hidden">
                <GalleryImages
                  images={imagesFiltered}
                  imagesVariations={productInfo?.imagesVariations}
                  currentImageVariation={currentVariation?.imageid}
                  views={productInfo?.data?.product?.views}
                  productId={product?.id}
                  noThumbnail={true}
                />
                <div className="flex justify-content-end gap-2 align-items-center">
                  {!isFringeBenefitsPage && (
                    <>
                      <FavoriteButton productId={product.id} nonAbsoluteFav={true} />{" "}
                      <ShareButton title={product.title} />
                    </>
                  )}
                </div>
              </div>

              {!isFringeBenefitsPage && <PromoterShareBox product={product} />}

              {productInfo?.data?.product?.promotionalPriceUnique && (
                <div className="flex gap-1 align-items-center mt-3">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="7.94173" cy="7.99984" r="5.84456" fill="#245AE5" />
                    <path
                      d="M6.14632 6.97915C6.60656 6.97915 6.97967 6.60607 6.97967 6.14583C6.97967 5.6856 6.60656 5.3125 6.14632 5.3125C5.68609 5.3125 5.31299 5.6856 5.31299 6.14583C5.31299 6.60607 5.68609 6.97915 6.14632 6.97915Z"
                      fill="#FAFBFC"
                    />
                    <path
                      d="M5.46774 9.82535C5.27247 10.0206 5.27247 10.3372 5.46774 10.5325C5.663 10.7277 5.97958 10.7277 6.17485 10.5325L10.5332 6.17411C10.7284 5.97885 10.7284 5.66227 10.5332 5.46701C10.3379 5.27174 10.0214 5.27174 9.82611 5.46701L5.46774 9.82535Z"
                      fill="#FAFBFC"
                    />
                    <path
                      d="M10.6882 9.85409C10.6882 10.3144 10.315 10.6874 9.85482 10.6874C9.39455 10.6874 9.02148 10.3144 9.02148 9.85409C9.02148 9.39389 9.39455 9.02075 9.85482 9.02075C10.315 9.02075 10.6882 9.39389 10.6882 9.85409Z"
                      fill="#FAFBFC"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8.98464 1.64652C8.40317 1.08397 7.4803 1.08397 6.89877 1.64652L6.29732 2.22838C6.18648 2.33561 6.03235 2.38569 5.87965 2.36409L5.05105 2.24689C4.24994 2.13357 3.50332 2.67603 3.36353 3.47294L3.21894 4.2972C3.1923 4.44911 3.09704 4.58021 2.96081 4.65249L2.22157 5.04471C1.50686 5.42393 1.22168 6.30163 1.577 7.02853L1.94451 7.78033C2.01224 7.91887 2.01224 8.08093 1.94451 8.21953L1.577 8.97133C1.22167 9.6982 1.50686 10.5759 2.22157 10.9551L2.96081 11.3473C3.09704 11.4196 3.1923 11.5507 3.21894 11.7027L3.36353 12.5269C3.50332 13.3238 4.24994 13.8663 5.05105 13.7529L5.87965 13.6357C6.03235 13.6141 6.18648 13.6643 6.29732 13.7715L6.89877 14.3533C7.4803 14.9159 8.40317 14.9159 8.98464 14.3533L9.5861 13.7715C9.69697 13.6643 9.85111 13.6141 10.0038 13.6357L10.8324 13.7529C11.6335 13.8663 12.3801 13.3238 12.5199 12.5269L12.6645 11.7027C12.6912 11.5507 12.7864 11.4196 12.9226 11.3473L13.6619 10.9551C14.3766 10.5759 14.6618 9.6982 14.3064 8.97133L13.939 8.21953C13.8712 8.08093 13.8712 7.91887 13.939 7.78033L14.3064 7.02853C14.6618 6.30163 14.3766 5.42393 13.6619 5.04471L12.9226 4.65249C12.7864 4.58021 12.6912 4.44911 12.6645 4.2972L12.5199 3.47294C12.3801 2.67603 11.6335 2.13357 10.8324 2.24689L10.0038 2.36409C9.85111 2.38569 9.69697 2.33561 9.5861 2.22838L8.98464 1.64652ZM7.5941 2.36525C7.7879 2.17773 8.09551 2.17773 8.28937 2.36525L8.89084 2.9471C9.22337 3.26879 9.68577 3.41903 10.1438 3.35423L10.9724 3.23703C11.2395 3.19926 11.4884 3.38008 11.535 3.64571L11.6796 4.46998C11.7595 4.92569 12.0452 5.31901 12.454 5.53586L13.1932 5.92808C13.4314 6.05448 13.5265 6.34705 13.408 6.58935L13.0405 7.3412C12.8374 7.75687 12.8374 8.243 13.0405 8.65867L13.408 9.41047C13.5265 9.6528 13.4314 9.94533 13.1932 10.0717L12.454 10.464C12.0452 10.6809 11.7595 11.0741 11.6796 11.5299L11.535 12.3541C11.4884 12.6197 11.2395 12.8006 10.9724 12.7628L10.1438 12.6456C9.68577 12.5808 9.22337 12.7311 8.89084 13.0527L8.28937 13.6346C8.09551 13.8221 7.7879 13.8221 7.5941 13.6346L6.99264 13.0527C6.66008 12.7311 6.1977 12.5808 5.7396 12.6456L4.911 12.7628C4.64396 12.8006 4.39509 12.6197 4.34849 12.3541L4.2039 11.5299C4.12397 11.0741 3.8382 10.6809 3.4295 10.464L2.69026 10.0717C2.45202 9.94533 2.35696 9.6528 2.4754 9.41047L2.84292 8.65867C3.0461 8.243 3.0461 7.75687 2.84292 7.3412L2.4754 6.58935C2.35696 6.34705 2.45202 6.05448 2.69026 5.92808L3.4295 5.53586C3.8382 5.31901 4.12397 4.92569 4.2039 4.46998L4.34849 3.64572C4.39509 3.38008 4.64396 3.19926 4.911 3.23703L5.7396 3.35423C6.1977 3.41903 6.66008 3.26879 6.99264 2.9471L7.5941 2.36525Z"
                      fill="#245AE5"
                    />
                  </svg>
                  <span
                    className="uppercase font-bold"
                    style={{ color: "#245AE5", fontSize: "10px", letterSpacing: "1px" }}
                  >
                    {localeText("goodOffer")}
                  </span>
                </div>
              )}

              {/*  <div
                className="my-5 text-sm"
                style={{ color: "#344563" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product?.description) }}
              ></div> */}
              <div className="flex flex-column xl:gap-2 gap-2 mt-4">
                <ProductPrice
                  productInfo={productInfo}
                  currency={currencySymbol}
                  currentVariation={currentVariation}
                  currentAttribute2Value={currentAttribute2Value}
                  currentAttribute1Value={currentAttribute1Value}
                />

                {!isFringeBenefitsPage && (
                  <ProductPromotionalPrice currency={currencySymbol} productInfo={productInfo} />
                )}
              </div>
              <div className="mb-4">
                <MinimalPrice
                  currency={currencySymbol}
                  currentVariation={currentVariation}
                  productInfo={productInfo}
                />
                {/* <ComparePriceButton
                  title={productInfo.data.product.title}
                  priceUnique={product.priceUnique}
                  promotionalPriceUnique={product.promotionalPriceUnique}
                /> */}
              </div>

              {!isFringeBenefitsPage && (
                <ProductVariations
                  attribute1Values={attribute1Values}
                  attribute2Values={attribute2Values}
                  checkAndSetCurrentAttribute1Value={checkAndSetCurrentAttribute1Value}
                  checkAndSetCurrentAttribute2Value={checkAndSetCurrentAttribute2Value}
                  currentAttribute1Value={currentAttribute1Value}
                  currentAttribute2Value={currentAttribute2Value}
                  productInfo={productInfo}
                />
              )}

              <ProductQuantity
                productInfo={productInfo}
                currentAttribute1Value={currentAttribute1Value}
                currentAttribute2Value={currentAttribute2Value}
                currentVariation={currentVariation}
              />
              <div className="flex flex-column gap-4 flex-wrap">
                {!product?.downloadable && (
                  <InputNumberStyled
                    htmlFor="product-qty"
                    labelTitle={localeText("quantity")}
                    value={productQty}
                    min={0}
                    max={
                      isFringeBenefitsPage
                        ? productInfo?.data.product?.amountInStock
                        : productInfo?.data?.attributes?.length === 0
                        ? product.amountInStock
                        : currentVariation
                        ? currentVariation.amountInStock
                        : 1
                    }
                    showButtons
                    onValueChange={(e) => setProductQty(e.value)}
                  />
                )}
                <div>
                  {product?.downloadable && (
                    <a
                      href={`${product?.urldownloadsample}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <h5 className="my-4 text-base">{localeText("clickForASample")}</h5>
                    </a>
                  )}
                  <div className="grid grid-nogutter no-wrap sm:wrap gap-3 w-full">
                    <div className="col-12 sm:col w-full">
                      <ButtonAddToCart
                        productInfo={productInfo}
                        currentVariation={currentVariation}
                        currentAttribute1Value={currentAttribute1Value}
                        currentAttribute2Value={currentAttribute2Value}
                        productQty={productQty}
                      />
                    </div>

                    <MiniAddToCart
                      productInfo={productInfo}
                      currentVariation={currentVariation}
                      currentAttribute1Value={currentAttribute1Value}
                      currentAttribute2Value={currentAttribute2Value}
                      productQty={productQty}
                      productSectionButton={true}
                    />
                  </div>
                </div>
              </div>
              {/* {isFreeExtracoin && <p className="mt-4">*{localeText("freeExtracoinExplanation")}</p>} */}
            </div>

            <hr className="my-4" />

            <ProductInfoFooter
              product={product}
              productInfo={productInfo}
              storeName={storeName}
              freightText={freightText}
              freightType={freightType}
              openStoreLocationModal={openStoreLocationModal}
            />
          </div>
        </div>
      </div>

      {!isFringeBenefitsPage && (
        <div className="my-8">
          <RelationedProducts productInfo={productInfo} />
        </div>
      )}

      <div className="my-8">
        <ProductDescription productInfo={productInfo} />
      </div>

      {!isFringeBenefitsPage && (
        <>
          <div className="my-8">
            <ProductQuestionsArea
              productInfoID={product.id}
              productOwner={product.user.username}
              productOwnerFirstName={product?.user?.firstname}
              productQuestionsRef={productQuestionsRef}
            />
          </div>
          <div className="my-8">
            <ProductRatingComments productInfoID={product.id} />
          </div>
        </>
      )}
    </div>
  );
};

export default ProductSection;
